import { useLayoutEffect, useRef, useState, type ReactNode } from "react"
import {
  type Column,
  type CellContext,
  type ColumnDef,
  type PaginationState,
  type Row,
  type RowSelectionState,
  type SortingState,
  type Updater,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table"
import { ArrowDown, ArrowUp, ChevronsUpDown } from "lucide-react"

import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { cn } from "@/lib/utils"

declare module "@tanstack/react-table" {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- required to match the merged interface's generic arity
  interface ColumnMeta<TData, TValue> {
    /** Applied to both the header and body cells of this column — use to pin a stable width (e.g. `"w-10"`) so columns stop reflowing as filtered/paginated content changes length. */
    className?: string
    /** Opts a column out of tanstack's own client-side sort (pair with `enableSorting: false` on the column def) in favor of a caller-driven sort — e.g. a column backed by a paginated server request, where re-sorting the currently loaded page client-side would be wrong. The header still renders as a sort button; clicking it calls `onToggle` instead of tanstack's toggle handler, and the icon reflects `direction` instead of `column.getIsSorted()`. */
    manualSort?: {
      direction: false | "asc" | "desc"
      onToggle: () => void
    }
    /** Clips this column's header and body content to one line ending in «…», with the full text in a native `title` tooltip while it is actually cut off. Needs a bounded column width (a `table-fixed` width, or `max-w-0` under `table-auto`) — an unbounded column just grows to fit and never truncates. Opt-in; omitted by every pre-existing column. */
    truncate?: boolean
  }
}

/**
 * One-line «…» clip for a `meta.truncate` column. The `title` is set on
 * hover rather than at render: only then can it compare `scrollWidth`
 * against `clientWidth` to show the tooltip just when the text really is
 * cut off, and read `textContent` so it also covers cells rendering a link
 * or button rather than a plain string.
 */
function TruncatedContent({ children }: { children: ReactNode }) {
  return (
    // A `span`, not a `div`: inside a sortable header it sits in a `<button>`,
    // which only permits phrasing content.
    <span
      className="block min-w-0 truncate"
      onMouseEnter={(e) => {
        const node = e.currentTarget
        // Descendants too: a `<button>` is an atomic inline box the outer
        // ellipsis can't cut into, so such a cell clips it with its own
        // `truncate` and the overflow shows up there, not on `node`.
        const clipped = [node, ...node.querySelectorAll("*")].some(
          (el) => el.scrollWidth > el.clientWidth
        )
        if (clipped) {
          node.title = node.textContent ?? ""
        } else {
          node.removeAttribute("title")
        }
      }}
    >
      {children}
    </span>
  )
}

/**
 * design-system.md's windowed pagination: first, last, current ± one
 * neighbour, "…" for any gap — at most seven entries regardless of page
 * count.
 */
function pageWindow(current: number, count: number): (number | "…")[] {
  if (count <= 7) return Array.from({ length: count }, (_, i) => i)
  const around = [current - 1, current, current + 1].filter(
    (i) => i > 0 && i < count - 1
  )
  const pages = [...new Set([0, ...around, count - 1])].sort((a, b) => a - b)
  const out: (number | "…")[] = []
  pages.forEach((page, i) => {
    if (i > 0 && page - (pages[i - 1] as number) > 1) out.push("…")
    out.push(page)
  })
  return out
}

/**
 * A header renders as a sort button only when both `getCanSort()` is true
 * and the header is a non-empty string — a select-all checkbox header (a
 * function, not a string) must not become a sort trigger.
 */
function isSortable<TData, TValue>(column: Column<TData, TValue>) {
  const header = column.columnDef.header
  if (typeof header !== "string" || header === "") return false
  return column.getCanSort() || column.columnDef.meta?.manualSort !== undefined
}

function SortIcon({ direction }: { direction: false | "asc" | "desc" }) {
  if (direction === "asc") return <ArrowUp className="size-3.5" />
  if (direction === "desc") return <ArrowDown className="size-3.5" />
  return <ChevronsUpDown className="size-3.5 text-muted-foreground/50" />
}

// design-system.md's "Primary list tables fill the available viewport
// height": AppShell/PageContainer don't establish a bounded flex-height
// chain today (every other page relies on the browser scrolling the whole
// document), so this measures the container's own distance from the
// viewport top instead of depending on one — self-contained regardless of
// which page renders it. The space kept free below it is measured, as in the
// prototype's DataTable: `<main>`'s bottom padding plus, for every ancestor
// up to it, its bottom padding and whatever follows the table there — a
// fixed 24px gutter ignored the wrapping Card's own padding and let the page
// scroll by ~16px.
const FILL_AVAILABLE_HEIGHT_MIN = 160

function reservedBelow(node: HTMLElement) {
  const main = node.closest("main")
  let reserved = main
    ? Number.parseFloat(getComputedStyle(main).paddingBottom) || 0
    : 0
  let descendant: HTMLElement = node
  let ancestor = node.parentElement
  while (ancestor && ancestor !== main) {
    const style = getComputedStyle(ancestor)
    reserved += Number.parseFloat(style.paddingBottom) || 0
    let following = 0
    for (let s = descendant.nextElementSibling; s; s = s.nextElementSibling) {
      const siblingStyle = getComputedStyle(s)
      reserved +=
        s.getBoundingClientRect().height +
        (Number.parseFloat(siblingStyle.marginTop) || 0) +
        (Number.parseFloat(siblingStyle.marginBottom) || 0)
      following++
    }
    // `rowGap` is the keyword "normal" outside flex/grid — NaN, counted as 0.
    reserved += (Number.parseFloat(style.rowGap) || 0) * following
    descendant = ancestor
    ancestor = ancestor.parentElement
  }
  return reserved
}

function useFillAvailableHeight(enabled: boolean) {
  const ref = useRef<HTMLDivElement>(null)
  const [maxHeight, setMaxHeight] = useState<number>()

  useLayoutEffect(() => {
    // No reset-to-undefined branch when `!enabled`: `maxHeight` is only ever
    // read from the render below while `fillAvailableHeight` is true, so a
    // stale value from a previous `true`->`false` toggle is inert.
    if (!enabled) return
    const node = ref.current
    if (!node) return
    const measure = () => {
      const top = node.getBoundingClientRect().top
      setMaxHeight(
        Math.max(
          window.innerHeight - top - reservedBelow(node),
          FILL_AVAILABLE_HEIGHT_MIN
        )
      )
    }
    measure()
    window.addEventListener("resize", measure)
    return () => window.removeEventListener("resize", measure)
  }, [enabled])

  return { ref, maxHeight }
}

/**
 * Shared composite wrapping `@tanstack/react-table` around `Table`/
 * `Pagination` — design-system.md's `DataTable` contract. Pagination is
 * fully controlled by the caller; sorting is uncontrolled once seeded via
 * `defaultSorting`; row selection and the loading skeleton are opt-in.
 */
export function DataTable<TData, TValue>({
  columns,
  data,
  isLoading = false,
  rowActions,
  onPageChangeStart,
  onPaginationChange,
  pagination,
  enablePagination = true,
  fillAvailableHeight = false,
  enableRowSelection,
  rowSelection,
  onRowSelectionChange,
  getRowId,
  activeRowId,
  tableClassName,
  defaultSorting,
  footer,
}: {
  columns: ColumnDef<TData, TValue>[]
  data: TData[]
  isLoading?: boolean
  /** Row actions, rendered in a dedicated right-aligned column (always visible, not a hover-reveal overlay) — omitted entirely if every row returns nothing. */
  rowActions?: (row: Row<TData>) => ReactNode
  onPageChangeStart?: () => void
  /** Required unless `enablePagination={false}` — every pre-existing consumer keeps passing both. */
  onPaginationChange?: (pagination: PaginationState) => void
  pagination?: PaginationState
  /** Default `true` (every pre-existing consumer keeps controlled pagination unchanged). `false` shows every row with no page slicing and no `Pagination` control — Documents' primary list and advanced-search results. */
  enablePagination?: boolean
  /** Stretches the table's own scroll container down to the viewport's bottom edge instead of letting the page grow — design-system.md's "Primary list tables fill the available viewport height." Opt-in; omitted by every pre-existing consumer. */
  fillAvailableHeight?: boolean
  /** Omit entirely for a table with no selection column at all. */
  enableRowSelection?: boolean | ((row: Row<TData>) => boolean)
  rowSelection?: RowSelectionState
  onRowSelectionChange?: (rowSelection: RowSelectionState) => void
  getRowId?: (row: TData) => string
  /** Row whose id (per `getRowId`) gets the design-system primary-blue background tint — documents.md's "the active row is highlighted" (the document currently open in a modal above this table). Omitted by every consumer that has no such concept. */
  activeRowId?: string
  /** Applied to the underlying `<table>` — pass `"table-fixed"` alongside per-column `meta.className` widths to stop columns reflowing as content changes. */
  tableClassName?: string
  /** Initial sort, e.g. `[{ id: "dateISO", desc: true }]`. Sorting is uncontrolled from here on. */
  defaultSorting?: SortingState
  /** Rendered after the last row, inside the same scroll container `fillAvailableHeight` bounds — a "Завантажити ще" button, for example, scrolls with the list instead of landing outside the viewport-bounded area. Omitted by every consumer that doesn't need one. */
  footer?: ReactNode
}) {
  const [sorting, setSorting] = useState<SortingState>(defaultSorting ?? [])
  // Only relevant when `enablePagination` is false and the caller therefore
  // doesn't supply controlled pagination state at all — tanstack's pagination
  // row model still wants *some* state internally even though nothing here
  // ever reads a slice of it (see `rows` below, which reads the sorted model
  // directly in that mode).
  const [fallbackPagination, setFallbackPagination] = useState<PaginationState>(
    { pageIndex: 0, pageSize: data.length || 10 }
  )
  const paginationState = pagination ?? fallbackPagination
  const handlePaginationChange = onPaginationChange ?? setFallbackPagination
  const { ref: fillHeightRef, maxHeight } = useFillAvailableHeight(
    fillAvailableHeight
  )
  const actionColumn = rowActions
    ? undefined
    : columns.find((column) => column.id === "actions")
  const tableColumns = actionColumn
    ? columns.filter((column) => column.id !== "actions")
    : columns
  const getRowActions =
    rowActions ??
    (actionColumn?.cell
      ? (row: Row<TData>) =>
          flexRender(actionColumn.cell, { row } as CellContext<TData, TValue>)
      : undefined)

  const table = useReactTable({
    columns: tableColumns,
    data,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    getRowId,
    enableRowSelection,
    onRowSelectionChange: (updater: Updater<RowSelectionState>) => {
      if (!onRowSelectionChange) return
      const next =
        typeof updater === "function" ? updater(rowSelection ?? {}) : updater
      onRowSelectionChange(next)
    },
    onPaginationChange: (updater) => {
      onPageChangeStart?.()
      const next =
        typeof updater === "function" ? updater(paginationState) : updater
      handlePaginationChange(next)
    },
    state: { pagination: paginationState, rowSelection, sorting },
  })

  const rows = enablePagination
    ? table.getRowModel().rows
    : table.getSortedRowModel().rows
  const pageCount = table.getPageCount()

  // design-system.md's "canonical action surface": actions render in a
  // dedicated, right-aligned column at the right edge of the table — not a
  // hover-reveal overlay on the last existing column. The whole column is
  // omitted if no row in the current page has any action to show.
  const rowActionContents = getRowActions
    ? rows.map((row) => getRowActions(row))
    : []
  // While loading there are no real rows yet to inspect, so the skeleton
  // can't tell whether any of them would have actions — assume yes whenever
  // `rowActions` is passed at all, so the skeleton's own column count
  // matches the real table it's standing in for instead of shifting layout
  // once data arrives.
  const hasActionsColumn = isLoading
    ? getRowActions !== undefined
    : rowActionContents.some(
        (content) => content !== null && content !== undefined && content !== false
      )

  return (
    <div>
      <div
        className={fillAvailableHeight ? "overflow-y-auto" : undefined}
        ref={fillAvailableHeight ? fillHeightRef : undefined}
        style={fillAvailableHeight ? { maxHeight } : undefined}
      >
        <Table className={tableClassName}>
          <TableHeader
            className={
              fillAvailableHeight ? "sticky top-0 z-20 bg-background" : undefined
            }
          >
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const truncate = header.column.columnDef.meta?.truncate
                  const label = flexRender(
                    header.column.columnDef.header,
                    header.getContext()
                  )
                  return (
                    <TableHead
                      className={header.column.columnDef.meta?.className}
                      key={header.id}
                    >
                      {header.isPlaceholder ? null : isSortable(header.column) ? (
                        <button
                          aria-label={`Сортувати за «${header.column.columnDef.header as string}»`}
                          className={cn(
                            "-mx-2 inline-flex items-center gap-1 rounded px-2 py-1 hover:bg-muted",
                            // `-mx-2` widens the button past the cell's own
                            // content box by the same 1rem its padding adds,
                            // so the cap is that much over 100%.
                            truncate && "max-w-[calc(100%+1rem)]"
                          )}
                          onClick={
                            header.column.columnDef.meta?.manualSort?.onToggle ??
                            header.column.getToggleSortingHandler()
                          }
                          type="button"
                        >
                          {truncate ? <TruncatedContent>{label}</TruncatedContent> : label}
                          <SortIcon
                            direction={
                              header.column.columnDef.meta?.manualSort?.direction ??
                              header.column.getIsSorted()
                            }
                          />
                        </button>
                      ) : truncate ? (
                        <TruncatedContent>{label}</TruncatedContent>
                      ) : (
                        label
                      )}
                    </TableHead>
                  )
                })}
                {hasActionsColumn && (
                  // documents.md: an explicit width, unlike the other
                  // implicit-width columns — `table-fixed` doesn't grow a
                  // column to fit its content, so an unset width lets the
                  // row's buttons bleed left into the neighbouring cell.
                  // 288px (`w-72`, widened 01.10.2026 from `w-64`/256px: the
                  // «Не відправлено» pair needs 166 + 8 + 95 = 269px), 96px
                  // below `lg` where the action labels collapse to `sr-only`.
                  <TableHead className="w-72 text-right max-lg:w-24">
                    <span className="sr-only">Дії</span>
                  </TableHead>
                )}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading
              ? Array.from(
                  { length: enablePagination ? paginationState.pageSize : 5 },
                  (_, i) => (
                    <TableRow key={`skeleton-${i}`}>
                      {tableColumns.map((column, j) => (
                        <TableCell className={column.meta?.className} key={j}>
                          <Skeleton className="h-4 w-full" />
                        </TableCell>
                      ))}
                      {hasActionsColumn && (
                        <TableCell className="text-right">
                          <Skeleton className="ml-auto h-4 w-16" />
                        </TableCell>
                      )}
                    </TableRow>
                  )
                )
              : rows.map((row, rowIndex) => (
                  <TableRow
                    className={
                      activeRowId !== undefined && row.id === activeRowId
                        ? "bg-primary/5"
                        : undefined
                    }
                    key={row.id}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        className={cell.column.columnDef.meta?.className}
                        key={cell.id}
                      >
                        {cell.column.columnDef.meta?.truncate ? (
                          <TruncatedContent>
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </TruncatedContent>
                        ) : (
                          flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )
                        )}
                      </TableCell>
                    ))}
                    {hasActionsColumn && (
                      <TableCell className="text-right">
                        {rowActionContents[rowIndex] && (
                          <div className="flex items-center justify-end gap-1">
                            {rowActionContents[rowIndex]}
                          </div>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
          </TableBody>
        </Table>
        {footer}
      </div>
      {enablePagination && pageCount > 1 && (
        <Pagination className="mt-4">
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                aria-disabled={!table.getCanPreviousPage()}
                className={
                  !table.getCanPreviousPage()
                    ? "pointer-events-none opacity-50"
                    : ""
                }
                href="#"
                onClick={(e) => {
                  e.preventDefault()
                  if (table.getCanPreviousPage()) table.previousPage()
                }}
                text="Попередня"
              />
            </PaginationItem>
            {pageWindow(paginationState.pageIndex, pageCount).map((item, i) =>
              item === "…" ? (
                <PaginationItem key={`gap-${i}`}>
                  <PaginationEllipsis />
                </PaginationItem>
              ) : (
                <PaginationItem key={item}>
                  <PaginationLink
                    href="#"
                    isActive={item === paginationState.pageIndex}
                    onClick={(e) => {
                      e.preventDefault()
                      table.setPageIndex(item)
                    }}
                  >
                    {item + 1}
                  </PaginationLink>
                </PaginationItem>
              )
            )}
            <PaginationItem>
              <PaginationNext
                aria-disabled={!table.getCanNextPage()}
                className={
                  !table.getCanNextPage()
                    ? "pointer-events-none opacity-50"
                    : ""
                }
                href="#"
                onClick={(e) => {
                  e.preventDefault()
                  if (table.getCanNextPage()) table.nextPage()
                }}
                text="Наступна"
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}
    </div>
  )
}
